/* eslint @typescript-eslint/no-duplicate-enum-values: off */

/**
 * Enum that represents all available icons.  A string enum is used so that we can map the
 * enum key to one of four options:
 * 1. Values with a '#' indicate an svg icon on the loaded svg sprite sheet
 * 2. Values with a '/' or url are directly loaded via an img tag
 * 3. Values with a '!' indicate a separate icon component that gets loaded
 * 4. Values with none of the above are class based ico-moon or fa icons
 *
 * Additional application specific icons can be found in the separately deployable App.
 * An example would be the Hire specific icons that can be found in the HireIconType enum.
 *
 */
export enum IconType {
    Unknown = 'Unknown',
    Check = 'ico-moon ico-moon-checkmark',
    Key = 'ico-moon ico-moon-key',
    Signpost = 'ico-moon ico-moon-direction',
    Airplane = 'ico-moon ico-moon-paperplane',
    Person = 'ico-moon ico-moon-user3',
    StarBadge = '/Assets/star-badge.svg',
    IdCard = 'ico-moon ico-moon-vcard',
    Play = 'ico-moon ico-moon-play3',
    Cancel = 'ico-moon ico-moon-cancel-circle2',
    Edit = '#edit-pencil',
    Delete = '#garbage-can',
    Spinner = 'fa fa-spinner fa-spin',
    Eye = 'ico-moon ico-moon-eye',
    Envelope = 'ico-moon ico-moon-envelop',
    Upload = 'ico-moon ico-moon-file-upload2',
    ChainLink = 'ico-moon ico-moon-link',
    List = 'ico-moon ico-moon-list',
    Gear = 'ico-moon ico-moon-cog2',
    FeedbackCircle = '#feedbackCircle',
    Feedback = '#feedback',
    MagGlass = '/Assets/search-circle-icon.svg',
    GearPerson = '/Assets/person-gear-circle-icon.svg',
    AddPerson = 'fa fa-user-plus',
    People = '/Assets/people-circle-icon.svg',
    Briefcase = '/Assets/briefcase-circle-icon.svg',
    Info = 'ico-moon ico-moon-info',
    Info2 = 'ico-moon ico-moon-info2',
    Warning2 = 'ico-moon ico-moon-warning2',
    Istar = 'ico-moon ico-moon-star',
    FullStar = 'ico-moon ico-moon-star-full2',
    EmptyStar = 'ico-moon ico-moon-star-empty3',
    // Added a ! in front of these custom component icons
    Sliders = '!sliders',
    BAReport = '!baReport',
    Placard = '!placard',
    LineGraph = '!lineGraph',
    Clipboard = '!clipboard',
    PieChart = '!pieChart',
    BarGraph = '!barGraph',
    RutherfordAtom = '!rutherfordAtom',
    SpeechBubble = '!speechBubble',
    NumberedList = '!numberedList',
    Tactics = '!tactics',
    // end custom component icons
    Meter = 'ico-moon ico-moon-meter-fast',
    OpenFolder = 'ico-moon ico-moon-folder-open',
    ClosedFolder = 'fa fa-folder',
    StackEmpty = 'ico-moon ico-moon-stack-empty',
    Collaboration = 'ico-moon ico-moon-collaboration',
    Download = 'fa fa-download',
    LeftArrow = 'ico-moon ico-moon-arrow-left',
    ArrowDownThick = 'ico-moon ico-moon-arrow-down2',
    ArrowDownThin = 'ico-moon ico-moon-arrow-down4',
    Copy = '#copy',
    Close = 'ico-moon ico-moon-cross2',
    Redo2 = 'ico-moon ico-moon-redo2',
    AngleUp = 'fa fa-angle-up',
    AngleDown = 'fa fa-angle-down',
    CircleAngleUp = 'ico-moon ico-moon-circle-up2',
    CircleAngleDown = 'ico-moon ico-moon-circle-down2',
    StreamlineBriefcase = '#streamline-briefcase',
    StreamlinePerson = '#streamline-person',
    StreamlineFullStar = '#streamline-fullStar',
    StreamlineHalfStar = '#streamline-halfStar',
    StreamlineEmptyStar = '#streamline-emptyStar',
    DownloadBottom = '#download-bottom',
    TransparentLock = '#locked-icon-transparent',
    Hourglass = '#hourglass',
    SendEmail = '#send-email',
    CoachMarkArrowUpLeft = '#coach-mark-arrow-up-left',
    CoachMarkArrowUpRight = '#coach-mark-arrow-up-right',
    CoachMarkArrowDownLeft = '#coach-mark-arrow-down-left',
    CoachMarkArrowDownRight = '#coach-mark-arrow-down-right',
    AddCircle = '#add-circle',
    Filter = '#filter',
    CloseThin = '#close-thin',
    PersonOutline = '#person-outline',
    Calendar = '#calendar',
    InformationCircle = '#information-circle',
    ClosePanel = '#close-panel',
    OpenPanel = '#open-panel',
    BackChevron = '#back-chevron',
    // These are used to create SVGs for ref patterns to use on reports
    Adapter = '#reference-pattern-adapter',
    Altruist = '#reference-pattern-altruist',
    Analyzer = '#reference-pattern-analyzer',
    Artisan = '#reference-pattern-artisan',
    Captain = '#reference-pattern-captain',
    Collaborator = '#reference-pattern-collaborator',
    Controller = '#reference-pattern-controller',
    Craftsman = '#reference-pattern-artisan', // having duplicate might be an issue?
    Guardian = '#reference-pattern-guardian',
    Individualist = '#reference-pattern-individualist',
    Maverick = '#reference-pattern-maverick',
    Operator = '#reference-pattern-operator',
    Persuader = '#reference-pattern-persuader',
    Promoter = '#reference-pattern-promoter',
    Scholar = '#reference-pattern-scholar',
    Specialist = '#reference-pattern-specialist',
    Strategist = '#reference-pattern-strategist',
    Venturer = '#reference-pattern-venturer',
    // New "pi-icon-sprite.svg" icons
    SendEmailGroup = '#send-email-group',
    PersonSimple = '#person-simple',
    ListToDo = '#list-to-do',
    PiLogoRedWithName = '#pi-logo-red-with-name',
    PiLogoWhiteWithName = '#pi-logo-white-with-name',
    PiLogoWhite = '#pi-logo-white',
    MessageTyping = '#message-typing',
    PerformanceIncrease = '#performance-increase',
    Currency = '#currency',
    TeamMeeting = '#team-meeting',
    Trash = '#trash',
    ClosePeek = '#close-peek',
    Add = '#add',
    ActionEllipsis = '#action-ellipsis',
    Learn = '#learn',
    LearnNew = '#learnNew',
    PersonTearDrop = '#person-tearDrop',
    CheckMarkCircle = '#checkmark-circle',
    ChevronDown = '#chevron-down',
    SessionDelete = '#new-delete',
    SessionEdit = '#new-edit',
    LockedIcon = '#locked-icon',
    UnlockedIcon = '#unlocked-icon',
    ArrowRight = '#arrow-right',
    ArrowRightWithLine = '#arrow-right-with-line',
    CircleLeft = '#circle-left',
    BentArrow = '#bent-arrow',
    TeamsCircle = '#teams-circle-person',
    HideEye = '#hide-eye',
    ShowEye = '#show-eye',
    ReadEmail = '#read-email',
    TeamCircleNew = '#team-circle-new',
    TeamDiscovery = '#team-discovery',
    Print = '#print',
    TeamDetails = '#team-details',
    FilledDownArrow = '#filled-down-arrow',
    HeadQuestionMark = '#head-question-mark',
    ThumbUp = '#thumb-up',
    ThumbDown = '#thumb-down',
    FeedbackReceived = '#feedback-received',
    MultiColorCircleCheckMark = '#multi-color-circle-check-mark',
    // TeamConfiguartionIcons:
    TeamAdapting = '#teamConfig-adapting',
    TeamAnchoring = '#teamConfig-anchoring',
    TeamBolstering = '#teamConfig-bolstering',
    TeamCultivating = '#teamConfig-cultivating',
    TeamExecuting = '#teamConfig-executing',
    TeamExploring = '#teamConfig-exploring',
    TeamPathfinding = '#teamConfig-pathfinding',
    TeamProducing = '#teamConfig-producing',
    TeamStabilizing = '#teamConfig-stabilizing',
    TeamUnknown = '#team-strategy-unknown',
    // StrategyConfiguartionIcons:
    StrategyAdapting = '#strategyConfig-adapting',
    StrategyAnchoring = '#strategyConfig-anchoring',
    StrategyBolstering = '#strategyConfig-bolstering',
    StrategyCultivating = '#strategyConfig-cultivating',
    StrategyExecuting = '#strategyConfig-executing',
    StrategyExploring = '#strategyConfig-exploring',
    StrategyPathfinding = '#strategyConfig-pathfinding',
    StrategyProducing = '#strategyConfig-producing',
    StrategyStabilizing = '#strategyConfig-stabilizing',
    StrategyExploringAndStabilizing = '#strategyConfig-balancing',
    StrategyCultivatingAndProducing = '#strategyConfig-balancing',
    StrategyUnknown = '#team-strategy-unknown',
    // Analytic Byte Icons:
    LightBulb = '/Assets/bulb.svg',
    // Hire Related Icons
    /* These icons are used and were created by the Hire team.  They are defined in the pi-hire-icon-sprite.svg.
       These icons are used by components in client-core which is why their types are defined here.  
    */
    Bookmark = '#bookmark',
    BookmarkSelected = '#bookmark-selected',
    HeadphonesCustomerSupportQuestion = '#headphones-customer-support-question',
    AccountingCalculator = '#accounting-calculator',
    PiLogoBlackWithName = '#pi-logo-black-with-name',
    ConversationChat = '#conversation-chat',
    // End Hire related Icons
    SortUp = '#sort-up',
    SortDown = '#sort-down',
    SortEmpty = '#sort-empty',
    CircleCheckMark = '#circle-check-mark',
    // Diagnose Icons:
    SurveyTypeOrganization = '#survey-type-organization',
    SurveyTypePeople = '#survey-type-people',
    SurveyTypeManager = '#survey-type-manager',
    SurveyTypeJob = '#survey-type-job',
    SurveyTypeEngagement = '#survey-type-engagement',
    Clock = '#clock',
    Bolt = '#bolt',
    SurveyCircleCheck = '#survey-circle-check',
    CopyText = '#copy-text'
}
